<template>
  <div class="screening-list-item d-flex align-items-center flex-wrap" @click="$emit('onRowClicked', screening)">

    <div :class="{'col-lg-2': canViewKioskSerial, 'col-lg-3': !canViewKioskSerial}" class="col-sm-12 col-print-3 promotion-date-span">
      <div v-if="screeningIntervalSameDay(screening)">
        <div>
          {{ moment(screening.start).format('YYYY-MM-DD')}}
        </div>
        <div>
          {{ `${moment(screening.start).format('HH:mm')} - ${moment(screening.end).format('HH:mm')} `}}
        </div>
      </div>
      <div v-else>
        <div>
          {{ moment(screening.start).format('YYYY-MM-DD HH:mm')}}
        </div>
        <div>
          {{ moment(screening.end).format('YYYY-MM-DD HH:mm') }}
        </div>
      </div>
    </div>

    <div :class="{'col-lg-3': canViewScreeningAudiologyProvider, 'col-lg-4': !canViewScreeningAudiologyProvider}" class="col-print-6 col-sm-12 promotion-facility-span d-flex flex-column">
      <span> {{screening.facility ? screening.facility.name : $t('agenda.screenings.no_facility')}}
        <span v-if="screening.facility && (facilityStatus(screening.facility) === 1 || facilityStatus(screening.facility) === 2)" class="entity-status">
          <va-popover
            v-if="facilityStatus(screening.facility) === 1"
            :message="$t('entities.full_status.1')"
            placement="right">
            <span class="i-evollu-status-pending"></span>
          </va-popover>
          <va-popover
            v-if="facilityStatus(screening.facility) === 2"
            :message="$t('entities.full_status.2')"
            placement="right">
            <span class="i-evollu-status-inactive"></span>
          </va-popover>
        </span>
      </span>
      <span class="screening-facility-address" v-if="screening.facility">
        {{getFacilityAddressInformation(screening.facility)}}
      </span>
    </div>

    <div :class="{'col-lg-2': canViewKioskSerial, 'col-lg-3': !canViewKioskSerial}" class="col-sm-12 col-print-3 promotor-span">
      <span>{{ presentPromotor(screening.user)}} &nbsp;</span>
      <va-popover v-if="canUpdatePromotion(screening.end) && canUpdatePromotor(screening) && !hasPermissionToUpdate(screening)" placement="left" :message="$t('agenda.screenings.edit_promotor_tooltip')">
        <i class="fa fa-pencil custom-pen" @click.prevent="$emit('editScreeningPerson', screening)"></i>
      </va-popover>
    </div>

    <div v-if="canViewKioskSerial" class="no-print kiosk-span col-sm-12 col-lg-2">
      {{ presentKiosk(screening) }}
    </div>

    <div v-if="canViewScreeningAudiologyProvider" class="no-print screening-provider-span col-sm-12 col-lg-2">
      {{ presentScreeningProvider(screening) }}
      <span v-if="presentScreeningProviderStatus(screening) === 1 || presentScreeningProviderStatus(screening) === 2" class="entity-status">
        <va-popover
          v-if="presentScreeningProviderStatus(screening) === 1"
          :message="$t('entities.full_status.1')"
          placement="right">
          <span class="i-evollu-status-pending"></span>
        </va-popover>
        <va-popover
          v-if="presentScreeningProviderStatus(screening) === 2"
          :message="$t('entities.full_status.2')"
          placement="right">
          <span class="i-evollu-status-inactive"></span>
        </va-popover>
      </span>
    </div>

    <div class="offset-sm-3 col-sm-6 offset-lg-0 col-lg-1 pr-0 promotion-action-buttons mt-2 mt-md-0 mb-2 mb-md-0">

      <div class="d-flex justify-content-end align-items-center">

        <va-popover v-if="!screeningHasEnded(screening)" placement="left" :message="$t('agenda.screenings.view_appointments_tooltip')">
          <div class="action-item-wrapper mb-1" @click.prevent="$emit('openAppointments', screening)">
            <i class="fa fa-calendar colored-icon"></i>
          </div>
        </va-popover>

        <va-popover v-if="screening.session_uri && !screeningHasStarted(screening)" placement="left" :message="$t('agenda.screenings.view_leads_tooltip')">
          <div class="action-item-wrapper ml-3 mb-1" @click.prevent="$emit('openLeadsListing', screening)">
            <i class="fa fa-bullseye mb-1" :class="{'colored-icon': screening.report_id, 'uncolored-icon': !screening.report_id}"></i>
          </div>
        </va-popover>

        <!-- User Details Action Dropdown -->
        <va-dropdown
          v-if="displayDropdown"
          class="custom-dropdown"
          offset="5rem, 16px"
          fixed>
          <span slot="anchor">
            <slot>
              <div class="pl-3 pr-2">
                <span class="fa fa-ellipsis-v success" style="font-size: 1.5rem;"></span>
              </div>
            </slot>
          </span>
          <div class="contact-dropdown__content pl-3 pr-2 pt-2 pb-2">

            <div v-if="canUpdatePromotion(screening.end) && hasPermissionToUpdate(screening)"
                class="action-item-wrapper dropdown-item-wrapper"
                @click.prevent="$emit('updateScreening', screening)">
              <i class="fa fa-pencil custom-pen"></i>&nbsp;
              {{$t('agenda.screenings.labels.edit')}}
            </div>

            <div class="action-item-wrapper dropdown-item-wrapper" :class="{'dropdown-item-wrapper--disabled': !screening.user}" @click.prevent="handleQrCodeAction(screening)">
              <i class="fa fa-envelope"></i>&nbsp;
              {{$t('agenda.screenings.labels.send_qrcode')}}
            </div>

            <div v-if="screening.kiosk" class="action-item-wrapper dropdown-item-wrapper" @click.prevent="copyKioskSerial(screening)">
              <i class="ion ion-md-clipboard"></i>&nbsp;
              {{$t('agenda.screenings.labels.copy_serial')}}
            </div>

            <div v-if="hasPermissionToViewReport(screening)" class="action-item-wrapper dropdown-item-wrapper" :class="{'dropdown-item-wrapper--disabled': !screening.report_id && screeningHasStarted(screening) || !screening.user}" @click.prevent="handleReportAction(screening)">
              <div v-if="screening.report_id">
                <i class="fa fa-file"></i>&nbsp;
                {{$t('agenda.screenings.labels.view_screening_report')}}
              </div>

              <div v-else >
                <i class="fa fa-file"></i>&nbsp;
                {{$t('agenda.screenings.labels.close_screening_report')}}
              </div>
            </div>

          </div>
        </va-dropdown>
        <!-- End User Details Actions Dropdown -->
      </div>
    </div>

  </div>
</template>
<script>
import IsFromEvollu from '@/mixins/IsFromEvolluMixin'

export default {
  props: {
    screening: {
      type: Object,
      required: true
    },
    facilities: {
      type: Array,
      required: true
    },
  },
  mixins: [
    IsFromEvollu
  ],
  computed: {
    canViewKioskSerial: function () {
      return this.isFromEvollu
    },
    canViewScreeningAudiologyProvider: function () {
      return this.isFromEvollu
    },
    ebookingService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.ebooking_server)
    },
    screeningPermissions: function () {
      return this.ebookingService.user_permissions.filter(permission => permission.info_type === 200)
    },
    reportsPermissions: function () {
      return this.ebookingService.user_permissions.filter(permission => permission.info_type === 500)
    }
  },
  methods: {
    facilityStatus: function (facility) {
      let fullFacility = this.facilities.find(fac => fac.id === facility.id)
      if (fullFacility && fullFacility.entity) return fullFacility.entity.status
      return null
    },
    displayDropdown: function () {
      return (this.canUpdatePromotion(this.screening.end) && this.hasPermissionToUpdate(this.screening)) || this.screening.kiosk || this.hasPermissionToViewReport(this.screening)
    },
    handleQrCodeAction: function (screening) {
      if (screening.user) {
        this.$emit('sendQRCode', { screening })
      }
    },
    handleReportAction: function (screening) {
      if ((!screening.report_id && this.screeningHasStarted(screening)) || !screening.user) return
      this.$emit(screening.report_id ? 'openSessionReport' : 'generateSessionReport', { screening })
    },
    screeningIntervalSameDay (screening) {
      return this.moment(screening.start).isSame(this.moment(screening.end), 'day')
    },
    getFacilityAddressInformation: function (facility) {
      let address = ''
      if (facility && facility.address) {
        if (facility.address.line1) {
          address += facility.address.line1
        }
        if (facility.address.city) {
          address += `${address.length > 0 ? ' - ' : ''}${facility.address.city}`
        }
      }
      if (address.length > 0) {
        address = '(' + address + ')'
      }
      return address
    },
    presentPromotor: function (promotor) {
      return promotor ? promotor.name : `${this.$t('agenda.screenings.unassigned_promotor')}`
    },
    presentKiosk: function (screening) {
      return screening.kiosk ? screening.kiosk.name : this.$t('agenda.screenings.unassigned_kiosk')
    },
    copyKioskSerial: function (screening) {
      const serialNumber = document.createElement('textarea')
      serialNumber.value = screening.kiosk.serial
      document.body.appendChild(serialNumber)
      serialNumber.select()
      document.execCommand('copy')
      document.body.removeChild(serialNumber)
      this.showToast(this.$t('agenda.screenings.kiosk_serial_copied'), this.$store.state.successToastOptions)
    },
    canUpdatePromotor (screening) {
      return screening.facility
        ? this.screeningPermissions.some(p => p.access_type === 2 && (p.entity_id === null || p.entity_id === screening.facility.entity_id))
        : null
    },
    presentScreeningProvider (screening) {
      let authenticationService = this.$store.state.services.find(service => service.url === this.$store.state.ebooking_server)
      let screeningProvider = authenticationService.entities.find(entity => entity.id === screening.aud_provider_id)
      return screeningProvider ? screeningProvider.name : this.$t('agenda.screenings.labels.undefined_provider')
    },
    presentScreeningProviderStatus (screening) {
      let authenticationService = this.$store.state.services.find(service => service.url === this.$store.state.ebooking_server)
      let screeningProvider = authenticationService.entities.find(entity => entity.id === screening.aud_provider_id)
      return screeningProvider ? screeningProvider.status : null
    },
    hasPermissionToUpdate (screening) {
      return screening.facility
        ? this.screeningPermissions.some(p => p.access_type === 3 && (p.entity_id === null || p.entity_id === screening.facility.entity_id))
        : null
    },
    hasPermissionToViewReport (screening) {
      return screening.facility
        ? this.reportsPermissions.some(p => p.access_type >= 1 && (p.entity_id === null || p.entity_id === screening.facility.entity_id))
        : null
    },
    canUpdatePromotion (date) {
      return this.isFromEvollu ? true : this.moment(date).diff(this.moment(), 'day') >= 0
    },
    screeningHasStarted (screening) {
      return this.moment(screening.start).diff(this.moment()) >= 0
    },
    screeningHasEnded (screening) {
      return this.moment(screening.end).diff(this.moment()) <= 0
    }
  }
}
</script>

<style lang="scss" scoped>

  .screening-list-item {
    padding: 0.7em 1.25em;

    &:not(:last-child) {
      border-bottom: solid 1px #ddd;
    }

    .entity-status {
      .i-evollu-status-pending,
      .i-evollu-status-inactive {
        width: 14px;
        height: 14px;
        margin-left: 4px;
      }
    }
  }

</style>

<style lang="scss" scoped>

@media only screen and (max-width : 992px) {

  .promotion-date-span {
    display: flex;
    justify-content: center;
    order:1;
  }

  .promotor-span {
    display: flex;
    justify-content: center !important;
    order: 2;
  }

  .promotion-facility-span {
    display: flex;
    justify-content: center;
    order: 3;
    text-align: center;
  }

  .kiosk-span {
    display: flex;
    justify-content: center;
    order: 4;
  }

  .screening-provider-span {
    display: flex;
    justify-content: center;
    order: 5
  }

  .promotion-action-buttons {
    display: flex;
    justify-content: space-around;
    order: 6;
  }

  .screening-date-time{
    font-size: 15px;
    margin-left: 3px;
    margin-top: 2px;
  }

  .screening-facility-address{
    font-size: 15px;
    margin-top: 1px;
    margin-left: 2px;
  }
}

.action-item-wrapper {
  width: 100%;
  height: 100%;
}

.dropdown-item-wrapper {
  display: flex;
  align-items:center;
  justify-content: flex-start;
}

.dropdown-item-wrapper i {
  color: #29c4a9;
}

.colored-icon {
  color: #29c4a9;
}

.uncolored-icon {
  color: #c9c9c9;
}

</style>

<style lang="scss" scoped>
.dropdown-item-wrapper {
  width: 100%;

  &:hover {
    color: #29c4a9;
    cursor: pointer;
  }

  &.dropdown-item-wrapper--disabled {
    color: #dddada;

    &:hover {
      color: whitesmoke;
      cursor:not-allowed;
    }

    & i {
      color: #dddada;
    }
  }
}
</style>
